import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Pause, Play, Volume2, VolumeX } from 'lucide-react';
import { recordingsAPI } from '../../../api/client';
import { apiError } from '../../../lib/host';
import './Recordings.css';

const SPEEDS = [0.75, 1, 1.25, 1.5, 2];
const clock = (s) => (Number.isFinite(s) ? `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}` : '0:00');

/**
 * Plays a recording: the audio is fetched through the API with the user's sign-in when Play is first pressed (no
 * storage address, nothing reusable in a URL), then kept in this page only. Play / pause, seek, time, volume, speed.
 */
export default function AudioPlayer({ recordingId, duration, label = 'Call recording' }) {
  const audio = useRef(null);
  const [src, setSrc] = useState(null);
  const [state, setState] = useState('idle'); // idle | loading | ready | error
  const [error, setError] = useState('');
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [length, setLength] = useState(duration || 0);
  const [volume, setVolume] = useState(1);
  const [speed, setSpeed] = useState(1);

  useEffect(() => () => { if (src) URL.revokeObjectURL(src); }, [src]);
  useEffect(() => { if (audio.current) audio.current.playbackRate = speed; }, [speed, src]);
  useEffect(() => { if (audio.current) audio.current.volume = volume; }, [volume, src]);

  const toggle = async () => {
    if (state === 'loading') return;
    if (!src) {
      setState('loading');
      try {
        const res = await recordingsAPI.audio(recordingId);
        setSrc(URL.createObjectURL(res.data));
        setState('ready');
        setPlaying(true); // starts once the element has the audio (onCanPlay)
      } catch (err) {
        const message = err.response?.data instanceof Blob ? await err.response.data.text().then((t) => { try { return JSON.parse(t).detail; } catch { return null; } }) : null;
        setError(message || apiError(err, 'Could not load the recording'));
        setState('error');
      }
      return;
    }
    if (audio.current.paused) audio.current.play().catch(() => {}); else audio.current.pause();
  };

  return (
    <div className="rc-player" aria-label={label}>
      {src && (
        <audio ref={audio} src={src} preload="auto"
          onCanPlay={() => { if (playing && audio.current.paused) audio.current.play().catch(() => setPlaying(false)); }}
          onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)}
          onTimeUpdate={(e) => setTime(e.target.currentTime)} onLoadedMetadata={(e) => Number.isFinite(e.target.duration) && setLength(e.target.duration)} />
      )}
      <button type="button" className="rc-play" onClick={toggle} aria-label={playing ? 'Pause' : 'Play'} disabled={state === 'error'}>
        {state === 'loading' ? <Loader2 size={17} className="spin" /> : playing ? <Pause size={17} /> : <Play size={17} />}
      </button>
      <span className="rc-time">{clock(time)} / {clock(length)}</span>
      <input type="range" className="rc-seek" min={0} max={length || 0} step={0.1} value={Math.min(time, length || 0)} disabled={!src}
        aria-label="Playback position" onChange={(e) => { audio.current.currentTime = Number(e.target.value); setTime(Number(e.target.value)); }} />
      <button type="button" className="rc-icon" aria-label={volume ? 'Mute' : 'Unmute'} onClick={() => setVolume(volume ? 0 : 1)}>
        {volume ? <Volume2 size={16} /> : <VolumeX size={16} />}
      </button>
      <input type="range" className="rc-volume" min={0} max={1} step={0.05} value={volume} aria-label="Volume" onChange={(e) => setVolume(Number(e.target.value))} />
      <select className="rc-speed" value={speed} onChange={(e) => setSpeed(Number(e.target.value))} aria-label="Playback speed">
        {SPEEDS.map((s) => <option key={s} value={s}>{s}x</option>)}
      </select>
      {state === 'error' && <small className="rc-error" role="alert">{error}</small>}
    </div>
  );
}
